import React, { useState, useEffect } from 'react';
import { PRICING_CONSTANTS, formatCurrency, calculateISODiscount } from '../api/pricingService';
import axios from '../api/axiosClient';
import { getAllISOStandardsPublic } from '../api/isoStandardsService';

function PricingOverview() {
  const [user, setUser] = useState(null);
  const [selectedISOs, setSelectedISOs] = useState([]);
  const [isoStandards, setISOStandards] = useState([]);
  const [includeCPD, setIncludeCPD] = useState(false);
  const [includePCAF, setIncludePCAF] = useState(false);
  const [paidServices, setPaidServices] = useState({
    membership: false,
    cpd: false,
    pcaf: false,
    iso_standards: []
  });

  useEffect(() => {
    fetchISOStandards();
    // Only fetch authenticated data if user has a token
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser();
      fetchPaidServices();
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/users/me');
      setUser(response.data);
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  };

  const fetchISOStandards = async () => {
    try {
      const response = await getAllISOStandardsPublic();
      setISOStandards(response.data);
    } catch (err) {
      console.error('Error fetching ISO standards:', err);
    }
  };

  const fetchPaidServices = async () => {
    try {
      // Fetch what services the cert body has already paid for
      const response = await axios.get('/pricing/paid-services');
      if (response.data) {
        setPaidServices(response.data);
        setSelectedISOs(response.data.iso_standards || []);
        setIncludeCPD(response.data.cpd || false);
        setIncludePCAF(response.data.pcaf || false);
      }
    } catch (err) {
      console.error('Error fetching paid services:', err);
    }
  };

  const toggleISO = (isoId) => {
    if (selectedISOs.includes(isoId)) {
      setSelectedISOs(selectedISOs.filter(id => id !== isoId));
    } else {
      setSelectedISOs([...selectedISOs, isoId]);
    }
  };

  const calculateTotal = () => {
    let total = PRICING_CONSTANTS.MEMBERSHIP_ANNUAL_FEE;

    // ISO Standards - first 3 at full price, 4+ at discounted price
    selectedISOs.forEach((_, index) => {
      if (index < PRICING_CONSTANTS.ISO_DISCOUNT_THRESHOLD - 1) {
        total += PRICING_CONSTANTS.ISO_STANDARD_FEE;
      } else {
        total += PRICING_CONSTANTS.ISO_STANDARD_FEE_DISCOUNTED;
      }
    });

    // CPD & Assessments
    if (includeCPD) {
      total += PRICING_CONSTANTS.CPD_ANNUAL_FEE;
    }

    // PCAF
    if (includePCAF) {
      total += PRICING_CONSTANTS.PCAF_ANNUAL_FEE;
    }

    return total;
  };

  const handlePayment = async () => {
    try {
      const response = await axios.post('/pricing/create-checkout', {
        iso_standard_ids: selectedISOs,
        include_cpd: includeCPD,
        include_pcaf: includePCAF
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('Error creating checkout:', err);
      alert('Failed to create checkout: ' + (err.response?.data?.detail || err.message));
    }
  };

  const total = calculateTotal();
  const isoDiscount = calculateISODiscount(selectedISOs.length);

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ color: '#000', marginBottom: '10px' }}>Annual Pricing</h1>
        <p style={{ color: '#666' }}>
          Select the services you need and pay once per year for full access
        </p>
      </div>

      {/* Paid Services Status Banner */}
      {paidServices.membership && (
        <div style={{
          background: 'linear-gradient(135deg, #28a745 0%, #20873a 100%)',
          color: 'white',
          padding: '20px 30px',
          borderRadius: '12px',
          marginBottom: '30px',
          boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ fontSize: '32px' }}>--</div>
            <div>
              <h3 style={{ margin: 0, marginBottom: '5px' }}>Active Membership</h3>
              <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
                Your annual membership is active.
                {paidServices.iso_standards?.length > 0 && ` • ${paidServices.iso_standards.length} ISO Standard${paidServices.iso_standards.length > 1 ? 's' : ''}`}
                {paidServices.cpd && ' • CPD & Assessments'}
                {paidServices.pcaf && ' • PCAF Access'}
              </p>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '30px' }}>
        {/* Left Column - Service Selection */}
        <div>
          {/* Membership Fee */}
          <div style={{
            background: '#fff',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, marginBottom: '5px', color: '#D4AF37' }}>Annual Membership</h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                  Required for all certification bodies
                </p>
              </div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#000' }}>
                {formatCurrency(PRICING_CONSTANTS.MEMBERSHIP_ANNUAL_FEE)}
                <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal' }}>/year</span>
              </div>
            </div>
          </div>

          {/* ISO Standards */}
          <div style={{
            background: '#fff',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#D4AF37', marginBottom: '10px' }}>ISO Standards</h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
              {formatCurrency(PRICING_CONSTANTS.ISO_STANDARD_FEE)}/year per standard •
              {formatCurrency(PRICING_CONSTANTS.ISO_STANDARD_FEE_DISCOUNTED)}/year from 4th standard onwards
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
              {isoStandards.map((iso, index) => {
                const selected = selectedISOs.includes(iso.id);
                const position = selectedISOs.indexOf(iso.id) + 1;
                const isDiscounted = position >= PRICING_CONSTANTS.ISO_DISCOUNT_THRESHOLD;

                return (
                  <div
                    key={iso.id}
                    onClick={() => toggleISO(iso.id)}
                    style={{
                      padding: '15px',
                      border: selected ? '2px solid #D4AF37' : '2px solid #ddd',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: selected ? '#FFF9E6' : '#fff',
                      transition: 'all 0.3s',
                      position: 'relative'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'start', gap: '10px' }}>
                      <input
                        type="checkbox"
                        checked={selected}
                        readOnly
                        style={{ accentColor: '#D4AF37', marginTop: '2px' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                          {iso.code}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{iso.name}</div>
                        {iso.description && (
                          <div style={{
                            fontSize: '11px',
                            color: '#999',
                            marginTop: '2px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}>
                            {iso.description}
                          </div>
                        )}
                        <div style={{
                          fontSize: '11px',
                          color: selected && isDiscounted ? '#28a745' : '#D4AF37',
                          marginTop: '6px',
                          fontWeight: '600'
                        }}>
                          {selected
                            ? `#${position} • ${isDiscounted
                                ? formatCurrency(PRICING_CONSTANTS.ISO_STANDARD_FEE_DISCOUNTED)
                                : formatCurrency(PRICING_CONSTANTS.ISO_STANDARD_FEE)}/year`
                            : `${formatCurrency(PRICING_CONSTANTS.ISO_STANDARD_FEE)}/year`
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CPD & Assessments */}
          <div style={{
            background: '#fff',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, marginBottom: '10px', color: '#D4AF37' }}>
                  CPD & Company Assessments
                </h3>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#000', marginBottom: '15px' }}>
                  {formatCurrency(PRICING_CONSTANTS.CPD_ANNUAL_FEE)}
                  <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal' }}>/year</span>
                </div>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#666', fontSize: '14px', lineHeight: '1.8' }}>
                  <li>Full CPD library access for all your clients</li>
                  <li>Unlimited CPD assessments</li>
                  <li>Company assessment certificates</li>
                  <li>{formatCurrency(PRICING_CONSTANTS.CERTIFICATE_FEE_CPD)} per certificate fee</li>
                </ul>
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '10px' }}>
                  <input
                    type="checkbox"
                    checked={includeCPD}
                    onChange={(e) => setIncludeCPD(e.target.checked)}
                    style={{
                      width: '24px',
                      height: '24px',
                      accentColor: '#D4AF37',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ fontWeight: '600', fontSize: '16px' }}>Include</span>
                </label>
              </div>
            </div>
          </div>

          {/* PCAF */}
          <div style={{
            background: '#fff',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, marginBottom: '10px', color: '#D4AF37' }}>
                  Professional Competency Accreditation (PCAF)
                </h3>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#000', marginBottom: '15px' }}>
                  {formatCurrency(PRICING_CONSTANTS.PCAF_ANNUAL_FEE)}
                  <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal' }}>/year</span>
                </div>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#666', fontSize: '14px', lineHeight: '1.8' }}>
                  <li>Access to all PCAF competency courses</li>
                  <li>Unlimited learner enrollments</li>
                  <li>Competency assessment and accreditation</li>
                  <li>Professional certificates for learners</li>
                  <li>{formatCurrency(PRICING_CONSTANTS.CERTIFICATE_FEE_PCAF)} per certificate fee</li>
                </ul>
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '10px' }}>
                  <input
                    type="checkbox"
                    checked={includePCAF}
                    onChange={(e) => setIncludePCAF(e.target.checked)}
                    style={{
                      width: '24px',
                      height: '24px',
                      accentColor: '#D4AF37',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ fontWeight: '600', fontSize: '16px' }}>Include</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Summary */}
        <div>
          <div style={{
            background: '#fff',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'sticky',
            top: '20px'
          }}>
            <h3 style={{ color: '#000', marginBottom: '20px' }}>Annual Summary</h3>

            {/* Itemized Breakdown */}
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '2px solid #f0f0f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontWeight: '600' }}>Membership</span>
                <span style={{ fontWeight: '600' }}>{formatCurrency(PRICING_CONSTANTS.MEMBERSHIP_ANNUAL_FEE)}</span>
              </div>

              {selectedISOs.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontWeight: '600' }}>ISO Standards ({selectedISOs.length})</span>
                    <span style={{ fontWeight: '600' }}>
                      {formatCurrency(
                        selectedISOs.reduce((sum, _, index) => {
                          return sum + (index < PRICING_CONSTANTS.ISO_DISCOUNT_THRESHOLD - 1
                            ? PRICING_CONSTANTS.ISO_STANDARD_FEE
                            : PRICING_CONSTANTS.ISO_STANDARD_FEE_DISCOUNTED);
                        }, 0)
                      )}
                    </span>
                  </div>
                  {isoDiscount > 0 && (
                    <div style={{ fontSize: '12px', color: '#28a745', textAlign: 'right' }}>
                      Save {formatCurrency(isoDiscount)} with volume discount
                    </div>
                  )}
                </div>
              )}

              {includeCPD && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontWeight: '600' }}>CPD & Assessments</span>
                  <span style={{ fontWeight: '600' }}>{formatCurrency(PRICING_CONSTANTS.CPD_ANNUAL_FEE)}</span>
                </div>
              )}

              {includePCAF && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontWeight: '600' }}>PCAF Access</span>
                  <span style={{ fontWeight: '600' }}>{formatCurrency(PRICING_CONSTANTS.PCAF_ANNUAL_FEE)}</span>
                </div>
              )}
            </div>

            {/* Total */}
            <div style={{ marginBottom: '25px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Total Annual Cost</span>
                <span style={{ fontSize: '28px', fontWeight: 'bold', color: '#D4AF37' }}>
                  {formatCurrency(total)}
                </span>
              </div>
              <div style={{ fontSize: '12px', color: '#666', textAlign: 'right' }}>
                Billed annually
              </div>
            </div>

            {/* Payment Button */}
            <button
              onClick={handlePayment}
              disabled={total === PRICING_CONSTANTS.MEMBERSHIP_ANNUAL_FEE}
              style={{
                width: '100%',
                padding: '16px',
                background: total === PRICING_CONSTANTS.MEMBERSHIP_ANNUAL_FEE
                  ? '#ccc'
                  : 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                color: total === PRICING_CONSTANTS.MEMBERSHIP_ANNUAL_FEE ? '#666' : '#000',
                border: 'none',
                borderRadius: '8px',
                cursor: total === PRICING_CONSTANTS.MEMBERSHIP_ANNUAL_FEE ? 'not-allowed' : 'pointer',
                fontWeight: '700',
                fontSize: '16px'
              }}
            >
              {total === PRICING_CONSTANTS.MEMBERSHIP_ANNUAL_FEE
                ? 'Select services to continue'
                : 'Proceed to Payment'}
            </button>

            {/* Certificate Fees */}
            <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '2px solid #f0f0f0' }}>
              <h4 style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#666' }}>
                Certificate Fees (charged per certificate)
              </h4>
              <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: '#f9f9f9', borderRadius: '4px' }}>
                  <span>ISO Certificates</span>
                  <span style={{ fontWeight: '600' }}>{formatCurrency(PRICING_CONSTANTS.CERTIFICATE_FEE_ISO)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: '#f9f9f9', borderRadius: '4px' }}>
                  <span>CPD/Assessment Certificates</span>
                  <span style={{ fontWeight: '600' }}>{formatCurrency(PRICING_CONSTANTS.CERTIFICATE_FEE_CPD)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: '#f9f9f9', borderRadius: '4px' }}>
                  <span>PCAF Certificates</span>
                  <span style={{ fontWeight: '600' }}>{formatCurrency(PRICING_CONSTANTS.CERTIFICATE_FEE_PCAF)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PricingOverview;
